import { memo, useEffect, useRef, useState } from 'react'

import {
  useFocusHierarchy,
  useFocusNodeById,
  useProcessKey,
  useSetFocus,
} from '@please/lrud'
import { scroller } from 'react-scroll'

import { Direction } from '@/constants/focus'
import { scrollOptionsType } from '@/types/sliderTypes'
import { isPointerMode } from '@/utils/utils'

type CustomFocusProps = {
  focusId: string
  scrollOptions?: scrollOptionsType
  startFocusChild?: number
  needSetFocus?: boolean
  hasNestedScroll?: boolean
  isNestedScroll?: boolean
  disablePointer?: boolean
  resetStartFocusChild?: boolean
}
export const useCustomFocus = ({
  focusId,
  scrollOptions,
  startFocusChild,
  needSetFocus = true,
  hasNestedScroll,
  isNestedScroll,
  disablePointer,
  resetStartFocusChild,
}: CustomFocusProps) => {
  const focusNode = useFocusNodeById(focusId)
  const [defaultFocusChild, setDefaultFocusChild] = useState(
    startFocusChild || 0,
  )
  const [canUseStartFocusChild, setCanUseStartFocusChild] = useState(true)

  const setFocus = useSetFocus()

  useEffect(() => {
    if (!canUseStartFocusChild) {
      return
    }
    const children = focusNode?.children || []

    if (startFocusChild !== undefined && children.length) {
      const childToFocus = children[startFocusChild]
      setDefaultFocusChild(startFocusChild)
      needSetFocus && setFocus(childToFocus)
      resetStartFocusChild && setCanUseStartFocusChild(false)
      scrollOptions &&
        setTimeout(() => scroller.scrollTo(childToFocus, scrollOptions), 0)
    }
    //  else {
    //   children.length && setDefaultFocusChild(0)
    // }
  }, [startFocusChild, focusNode?.children?.length, canUseStartFocusChild])

  useEffect(() => {
    if (!isPointerMode || !focusNode || disablePointer) {
      return
    }
    const focusedChildIndex = focusNode.focusedChildIndex

    if (focusedChildIndex !== null && focusedChildIndex !== defaultFocusChild) {
      setDefaultFocusChild(focusedChildIndex)

      const focusNodeChildren = focusNode.children
      const isFirstChildIndex = focusedChildIndex === 0
      const isLastChildIndex =
        focusedChildIndex === focusNodeChildren.length - 1
      const needToScroll =
        (isFirstChildIndex && !isNestedScroll) ||
        (isLastChildIndex && !hasNestedScroll)
      scrollOptions &&
        needToScroll &&
        setTimeout(
          () =>
            scroller.scrollTo(
              focusNodeChildren[focusedChildIndex],
              scrollOptions,
            ),
          0,
        )
    }
  }, [isPointerMode, disablePointer, focusNode?.focusedChildIndex])

  const onMove = (e: MoveEvent) => {
    const nextFocusId = e.nextChildNode.focusId
    const currentChildIndex =
      e.direction === Direction.Forward
        ? Number(e.prevChildIndex) + 1
        : Number(e.prevChildIndex) - 1
    setDefaultFocusChild(currentChildIndex)
    scrollOptions && scroller.scrollTo(nextFocusId, scrollOptions)
  }

  const onSelectWithScroll = (
    nextFocusId: string,
    nextChildIndex: Nullable<number>,
  ) => {
    nextChildIndex !== null && setDefaultFocusChild(nextChildIndex)
    scrollOptions && scroller.scrollTo(nextFocusId, scrollOptions)
  }

  return {
    defaultFocusChild,
    onMove,
    setDefaultFocusChild,
    onSelectWithScroll,
    isFocused: Boolean(focusNode?.isFocused),
  }
}

const trapScrollContainers = ['BroadcastAsideElem']

function throttleScrollHandle(cb: (event: any) => void, wait: number) {
  let time = Date.now()
  return function (event: any) {
    if (time + wait - Date.now() < 0) {
      cb(event)
      time = Date.now()
    }
  }
}

export const PointerScroll = memo(() => {
  const processKey = useProcessKey()
  const focusHierarchy = useFocusHierarchy()
  const trapScrollFocusNodeRef = useRef<FocusNode | null>(null)

  useEffect(() => {
    if (!isPointerMode || !focusHierarchy.length) {
      return
    }
    trapScrollFocusNodeRef.current = null
    trapScrollContainers.some((el) => {
      const focusNode = focusHierarchy.find(({ focusId }) =>
        focusId.includes(el),
      )
      if (focusNode) {
        trapScrollFocusNodeRef.current = focusNode as FocusNode
      }
      return focusNode
    })
  }, [focusHierarchy])

  useEffect(() => {
    // window.addEventListener('mousewheel', scrollhandle)
    isPointerMode && window.addEventListener('wheel', scrollListener)
    return () => {
      isPointerMode && window.removeEventListener('wheel', scrollListener)
    }
  }, [isPointerMode])

  const scrollHandle = (event: any) => {
    const direction =
      (event.wheelDelta && event.wheelDelta > 0) ||
      (event.detail && event.detail < 0) ||
      false

    const trapScrollFocusNode = trapScrollFocusNodeRef.current

    if (trapScrollFocusNode) {
      if (!trapScrollFocusNode.isFocused) {
        return
      }

      const focusedChildIndex = trapScrollFocusNode.focusedChildIndex || 0
      const childrenLength = trapScrollFocusNode.children.length
      const canScrollTop = direction && focusedChildIndex > 0
      const canScrollDown = !direction && focusedChildIndex < childrenLength - 1

      if (!canScrollTop && !canScrollDown) {
        return
      }
    }

    direction ? processKey.up() : processKey.down()
  }

  const scrollListener = throttleScrollHandle(scrollHandle, 300)

  return null
})

PointerScroll.displayName = 'PointerScroll'
